import React, { useState, useEffect } from 'react';
import { fetchTaskDocuments, fetchTaskDocumentBlob } from '../../../../services/tasks/taskDocumentsService.js';
import { DownloadSimple } from '@phosphor-icons/react';



const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (iso) => {
    if (!iso) return '';
    try {
        return new Date(iso).toLocaleDateString('es-AR', {
            day: '2-digit', month: '2-digit', year: 'numeric'
        });
    } catch {
        return '';
    }
};

/**
 * Modal para listar los documentos de una tarea visibles al técnico.
 * @param {Object} task - Tarea con id
 * @param {boolean} isOpen - Si el modal está abierto
 * @param {Function} onClose - Callback para cerrar
 */
function DocumentsModal({ task, isOpen, onClose }) {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        if (!isOpen || !task?.id) return;

        let cancelled = false;
        setLoading(true);
        setError(null);
        setDocuments([]);

        fetchTaskDocuments(task.id)
            .then((docs) => {
                if (cancelled) return;
                setDocuments(docs.filter((d) => d.visibleToTechnician));
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message || 'Error al obtener documentos.');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [isOpen, task?.id]);

    const handleDownload = async (doc) => {
        setDownloading(doc.id);
        try {
            const { blob, fileName } = await fetchTaskDocumentBlob(task.id, doc);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (err) {
            alert(err.message || 'No se pudo descargar el documento.');
        } finally {
            setDownloading(null);
        }
    };

    if (!isOpen || !task) return null;

    return (
        <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-[9999] p-4">
            <div className="bg-surface rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6">
                    <div className="flex items-center mb-4">
                        <h3 className="text-lg font-semibold text-content">
                            Documentos de la tarea
                        </h3>
                    </div>

                    {loading && (
                        <div className="flex items-center justify-center py-8 text-content-muted text-sm">
                            Cargando documentos...
                        </div>
                    )}

                    {error && !loading && (
                        <div className="py-4 text-error text-sm text-center">
                            {error}
                        </div>
                    )}

                    {!loading && !error && documents.length === 0 && (
                        <div className="py-8 text-content-muted text-sm text-center">
                            No hay documentos disponibles.
                        </div>
                    )}

                    {!loading && !error && documents.length > 0 && (
                        <ul className="divide-y divide-line mb-4 max-h-72 overflow-y-auto">
                            {documents.map((doc) => (
                                <li key={doc.id} className="flex items-center gap-3 py-3">
                                    {doc.extension && (
                                        <span className="flex-shrink-0 text-xs font-bold text-content-muted bg-surface-2 px-1.5 py-0.5 rounded uppercase">
                                            {doc.extension}
                                        </span>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-content truncate">
                                            {doc.name}
                                        </p>
                                        <p className="text-xs text-content-muted">
                                            {[formatSize(doc.size), formatDate(doc.createdAt)]
                                                .filter(Boolean)
                                                .join(' · ')}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => handleDownload(doc)}
                                        disabled={downloading === doc.id}
                                        className="flex-shrink-0 hover:bg-surface-2 disabled:opacity-50 text-content px-3 py-1.5 rounded-lg transition-colors"
                                    >
                                        {downloading === doc.id ? (
                                            <svg
                                                className="animate-spin"
                                                width={20}
                                                height={20}
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                                            </svg>
                                        ) : (
                                            <DownloadSimple size={20} className="text-content" />
                                        )}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="flex justify-start">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-surface-2 hover:bg-line text-content rounded-lg transition-colors"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DocumentsModal;
